/**
 * Trader's Command Center Dashboard
 * Professional-grade layout: Portfolio Snapshot strip + 3-column content area
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../common/Layout';
import PortfolioSnapshot from './PortfolioSnapshot';
import PortfolioGlance from './PortfolioGlance';
import TopMoversWidget from './TopMoversWidget';
import MarketContextWidget from './MarketContextWidget';
import { usePortfolioKPIs } from '../../contexts/PortfolioKPIContext';
import { apiService } from '../../services/api';
import UnverifiedEmailBanner from '../auth/UnverifiedEmailBanner';
import { MagnifyingGlassIcon, BriefcaseIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts';
import InfoTooltip from '../common/InfoTooltip';

/**
 * Sparkline component for opportunity table
 */
const Sparkline: React.FC<{ data: Array<{ close: number }> }> = ({ data }) => {
  if (!data || data.length < 2) return <div style={{ width: 44, height: 20 }} />;

  const isUp = data[data.length - 1].close >= data[0].close;
  // Trader theme gain/loss tokens (recharts needs literal colours)
  const color = isUp ? '#5cc79a' : '#e08a7e';

  return (
    <div style={{ width: 44, height: 20 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 1, right: 1, bottom: 1, left: 1 }}>
          <YAxis domain={['dataMin', 'dataMax']} hide={true} />
          <Area
            type="monotone"
            dataKey="close"
            stroke={color}
            fill={color}
            fillOpacity={0.08}
            strokeWidth={1.25}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { kpis } = usePortfolioKPIs();
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Only show onboarding modal once we confirm user has no positions
  useEffect(() => {
    if (!kpis.loading && !onboardingChecked) {
      setOnboardingChecked(true);
      if (!kpis.activePositions?.length) {
        setShowOnboardingModal(true);
      }
    }
  }, [kpis.loading, kpis.activePositions, onboardingChecked]);

  // Opportunity Scanner state
  const [opportunities, setOpportunities] = useState<Awaited<ReturnType<typeof apiService.getOpportunities>> | null>(null);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [opportunityChartData, setOpportunityChartData] = useState<Record<string, { results: Array<{ close: number }> }>>({});

  // Fetch best-timing opportunities on mount (compact: 5 items)
  useEffect(() => {
    const fetchOpportunities = async () => {
      setOpportunitiesLoading(true);
      try {
        const data = await apiService.getOpportunities(5);
        setOpportunities(data);
      } catch (error) {
        console.error('Failed to fetch opportunities:', error);
        setOpportunities(null);
      } finally {
        setOpportunitiesLoading(false);
      }
    };
    fetchOpportunities();
  }, []);

  // Fetch sparkline chart data for opportunities
  useEffect(() => {
    const fetchChartDataForOpportunities = async () => {
      if (!opportunities?.stocks || opportunities.stocks.length === 0) return;

      const symbols = opportunities.stocks.map(s => s.symbol);
      const fromDate = new Date();
      fromDate.setMonth(fromDate.getMonth() - 1);

      try {
        const response = await apiService.getStockDataBatch(
          symbols,
          'day',
          fromDate.toISOString().split('T')[0]
        );
        setOpportunityChartData(response.data);
      } catch (error) {
        console.error('Failed to fetch sparkline data:', error);
      }
    };
    fetchChartDataForOpportunities();
  }, [opportunities?.stocks]);

  return (
    <Layout>
      <div className="command-center">
        <UnverifiedEmailBanner />
        {/* Row 1: Full-width Portfolio Snapshot KPI Strip */}
        <PortfolioSnapshot />

        {/* Row 2: Three-Column Content Area */}
        <div className="command-columns-3">
          {/* Column 1: Market Context */}
          <div className="zone-market-context">
            <MarketContextWidget vertical />
          </div>

          {/* Column 2: Top Movers + Compact Opportunity Scanner */}
          <div className="zone-scanner">
            <TopMoversWidget
              activePositions={kpis.activePositions || []}
              loading={kpis.loading}
              onPositionClick={(ticker) => navigate(`/portfolio/positions?ticker=${ticker}`)}
            />
            <div className="command-tile opportunity-scanner-tile">
              <div className="command-tile-header scanner-header">
                <span className="command-tile-title">Quick Picks</span>
                <span className="command-tile-sub" title="Same filters as the screener's default view: Income fit ≥ 30, Timing ≥ 30, your liquidity rule, no earnings in 7 days.">Screener defaults · best Timing first</span>
              </div>

              <div className="opportunity-scanner">
                {opportunitiesLoading ? (
                  <div className="opportunities-loading">Loading opportunities...</div>
                ) : opportunities?.stocks && opportunities.stocks.length > 0 ? (
                  <div className="position-pnl-table-container">
                    <table className="position-pnl-table">
                      <thead>
                        <tr>
                          <th>Symbol</th>
                          <th className="numeric-column">Price</th>
                          <th className="numeric-column">
                            <span className="qp-th">
                              Income fit
                              <InfoTooltip text="Is the income worth it, and can you trade it? Premium yield and its track record, out of 50 (same as the screener card)." />
                            </span>
                          </th>
                          <th className="numeric-column">
                            <span className="qp-th">
                              Timing
                              <InfoTooltip text="Is now a good time to sell a call? Based on setup signals and calendar risk." />
                            </span>
                          </th>
                          <th className="numeric-column">
                            <span className="qp-th">
                              Ann. Yield
                              <InfoTooltip text="Premium yield per year of the stock's best call (delta 0.15–0.30, 30–45 days out, open interest ≥ 100), on the share price." />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {opportunities.stocks.map((stock) => (
                          <tr
                            key={stock.symbol}
                            className="clickable-row"
                            onClick={() => navigate(`/screening?symbol=${stock.symbol}`)}
                          >
                            <td className="ticker-cell">
                              <div className="qp-sym">
                                <span className="qp-tk">{stock.symbol}</span>
                                <Sparkline data={opportunityChartData[stock.symbol]?.results?.slice(-20) || []} />
                              </div>
                            </td>
                            <td className="numeric-cell">{stock.current_price != null ? `$${stock.current_price.toFixed(2)}` : '—'}</td>
                            <td className="numeric-cell">
                              <span className="qp-score" title={stock.quality_score != null ? `Income fit: ${Math.round(stock.quality_score)} / 50` : 'Income fit: not scored'}>
                                {stock.quality_score != null ? Math.round(stock.quality_score) : '—'}
                              </span>
                            </td>
                            <td className="numeric-cell">
                              <span className="qp-score" title={stock.timing_score != null ? `Timing: ${Math.round(stock.timing_score)} / 50` : 'Timing: not scored'}>
                                {stock.timing_score != null ? Math.round(stock.timing_score) : '—'}
                              </span>
                            </td>
                            <td className="numeric-cell">
                              {stock.best_ann_yield != null ? `${stock.best_ann_yield.toFixed(0)}%` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="opportunities-loading">No opportunities found</div>
                )}
              </div>

              {opportunities?.stocks && opportunities.stocks.length > 0 && (
                <button
                  className="scanner-view-all-btn"
                  onClick={() => navigate('/screening')}
                >
                  View all {opportunities.total_available} in the Screener
                </button>
              )}
            </div>
          </div>

          {/* Column 3: Portfolio at a Glance */}
          <div className="zone-positions">
            <PortfolioGlance
              activePositions={kpis.activePositions || []}
              loading={kpis.loading}
              onNavigateToScreener={() => navigate('/screening')}
            />
          </div>
        </div>
      </div>

      {/* Onboarding Modal - Shows when user has no positions and hasn't dismissed */}
      {!kpis.activePositions?.length && !kpis.loading && showOnboardingModal && (
        <div className="onboarding-modal-overlay">
          <div className="onboarding-modal">
            <button
              className="onboarding-modal-close"
              onClick={() => setShowOnboardingModal(false)}
            >
              ×
            </button>

            <h2 className="onboarding-modal-title">Generate Income from Your Stocks</h2>

            <p className="education-tagline">
              Babylon gives you a structured framework to find, execute, and manage covered calls — so you can focus on decisions, not analysis paralysis.
            </p>

            <div className="education-steps">
              <div className="education-step">
                <span className="step-icon">
                  <MagnifyingGlassIcon />
                </span>
                <div className="step-content">
                  <strong>Find the Right Stock</strong>
                  <p>We surface stocks with attractive premiums that also pass fundamental and technical checks — so you're comfortable holding them.</p>
                </div>
              </div>
              <div className="education-step">
                <span className="step-icon">
                  <AdjustmentsHorizontalIcon />
                </span>
                <div className="step-content">
                  <strong>Pick Your Strike</strong>
                  <p>Choose options that match your goal: maximize income or reduce assignment risk. We show the trade-offs clearly.</p>
                </div>
              </div>
              <div className="education-step">
                <span className="step-icon">
                  <BriefcaseIcon />
                </span>
                <div className="step-content">
                  <strong>Track & Maintain</strong>
                  <p>Monitor your positions and know when to hold, roll, or close based on price action and time decay.</p>
                </div>
              </div>
            </div>

            <button
              className="education-cta-btn"
              onClick={() => navigate('/screening')}
            >
              Start Exploring Stocks
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
